import { Grid, Paper } from '@mui/material'

import { GetSupplierBookingQuery, useGetSupplierBookingQuery } from 'generated/graphql'
import { useIsBookingDisabled } from 'hooks'

import { ReadOnlyBookingAddressType, SupplierBookingAddresses } from './AddressDetails'
import { AllocatedDrivers } from './DriverDetails'
import SupplierMainBookingDetails, {
  SupplierBookingDetailsProps,
} from './MainBookingDetails/SupplierMainBookingDetails'
import { SupplierPackageDetails } from './PackageDetails'
import { BookingPackageDetailsType } from './PackageDetails/SupplierPackageDetails'

interface ISupplierBookingDetailsProps {
  bookingId: string
}

const SupplierBookingDetails = (props: ISupplierBookingDetailsProps) => {
  const { bookingId } = props

  // Queries
  const { data: bookingData, loading: loadingBooking } = useGetSupplierBookingQuery({
    variables: {
      bookingId: Number(bookingId),
    },
  })

  const isBasicDisabled = useIsBookingDisabled(bookingData?.supplierBooking)

  // Calculations
  const bookingDetails = (bookingData?: GetSupplierBookingQuery): SupplierBookingDetailsProps => {
    return {
      bookingId: bookingData?.supplierBooking?.id ?? '',
      statusLabel: bookingData?.supplierBooking?.statusLabel ?? '',
      customerName: bookingData?.supplierBooking?.customer.name ?? '',
      ourReference: bookingData?.supplierBooking?.ourReference ?? '',
      customerReference: bookingData?.supplierBooking?.customerReference ?? '',
      status: bookingData?.supplierBooking?.status,
    }
  }

  const packages = (bookingData?: GetSupplierBookingQuery): BookingPackageDetailsType[] => {
    if (!bookingData?.supplierBooking) {
      return []
    }

    return bookingData?.supplierBooking.packages.map((p) => ({
      id: p.id,
      tabsKey: p.id,
      weight: p.weight ?? '',
      width: p.width ?? '',
      height: p.height ?? '',
      quantity: p.quantity,
      details: p.name,
      reference: p.packageReference ?? '',
      type: p.typeDescription ?? '',
      length: p.length,
      collectionAddress: p.collection?.address.name ?? '',
      deliveryAddress: p.delivery?.address.name ?? '',
    }))
  }

  const bookingAddresses = (
    bookingData?: GetSupplierBookingQuery,
  ): ReadOnlyBookingAddressType[] => {
    if (!bookingData?.supplierBooking) {
      return []
    }

    return bookingData?.supplierBooking.addresses.map((a) => ({
      id: a.id,
      tabsKey: a.id,
      at: a.at ?? '',
      by: a.by ?? '',
      name: a.address.name,
      addressLine1: a.address.addressLine1,
      addressLine2: a.address.addressLine2 ?? '',
      postcode: a.address.postcode,
      city: a.address.city,
      country: a.address.country.name,
      type: a.type,
      note: a.note ?? '',
      telephoneNumber: a.address.telephoneNumber ?? '',
      email: a.address.email ?? '',
      contact: a.address.contact ?? '',
    }))
  }

  if (loadingBooking) {
    return <>Loading...</>
  }

  return (
    <Grid container spacing={1.5}>
      <Grid item xs={12}>
        <Paper sx={{ padding: 2 }} variant='outlined'>
          <SupplierMainBookingDetails
            bookingDetails={bookingDetails(bookingData)}
            loading={loadingBooking}
            disabled={isBasicDisabled}
          />
        </Paper>
      </Grid>
      <SupplierBookingAddresses addresses={bookingAddresses(bookingData)} />
      <Grid item xs={12} lg={7} xl={5}>
        <Paper sx={{ padding: 2 }} variant='outlined'>
          <SupplierPackageDetails packages={packages(bookingData)} />
        </Paper>
      </Grid>
      <Grid item xs={12} lg={6} xl={5}>
        <Paper sx={{ padding: 2 }} variant='outlined'>
          <AllocatedDrivers bookingId={bookingData?.supplierBooking?.id} />
        </Paper>
      </Grid>
    </Grid>
  )
}

export default SupplierBookingDetails
